import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from "framer-motion";
import './Carousel.css';
import { CarouselSnippet } from '../../snippets/CarouselSnippet/CarouselSnippet.js';
import Card from '../Card/Card';
import { Copy } from 'lucide-react';

const staticSlides = [
  { title: "pic1", description: "image description of pic1" },
  { title: "pic2", description: "image description of pic2" },
  { title: "pic3", description: "image description of pic3" },
  { title: "pic4", description: "image description of pic4" },
  { title: "pic5", description: "image description of pic5" },
  { title: "pic6", description: "image description of pic6" },
];

const BrokenImage = ({ title }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      background: '#eee',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#888',
      fontSize: '2rem',
      marginBottom: '10px'
    }}
    aria-label={`Broken image placeholder for ${title}`}
  >
    {title}
  </div>
);

const Carousel = () => {
  const snippetContainerRef = useRef(null);
  const [showScanning, setShowScanning] = useState(false);

  const copySnippet = () => {
    if (snippetContainerRef.current) {
      const range = document.createRange();
      range.selectNode(snippetContainerRef.current);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();

      setShowScanning(true);
      setTimeout(() => setShowScanning(false), 1000);
    }
  };

  const [slides] = useState(staticSlides);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef();

  const moveSlide = useCallback((offset) => {
    setCurrent(prev => {
      let next = prev + offset;
      if (next >= slides.length) next = 0;
      if (next < 0) next = slides.length - 1;
      return next;
    });
  }, [slides.length]);

  useEffect(() => {
    if (slides.length === 0) return;
    intervalRef.current = setInterval(() => moveSlide(1), 3000);
    return () => clearInterval(intervalRef.current);
  }, [slides.length, moveSlide]);

  const goToSlide = (idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setCurrent(idx);
    setTimeout(() => setAnimating(false), 500);
  };

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="carve-lean-body">
        <div className="carve-line">
          <div className="box-12">
            <div className="carve-carousel">
              <ul
                className="carve-slides"
                style={{ transform: `translateX(-${current * 100}%)` }}
                data-slides
              >
                {slides.map((slide, idx) => (
                  <li className="carve-slide" key={idx}>
                    <BrokenImage title={slide.title} />
                    <div className="carousel-caption">
                      <div className="carousel-caption-title">{slide.title}</div>
                      <div className="carousel-caption-desc">{slide.description}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="carve-carousel-dots" data-carve-carousel-dots>
              {slides.map((_, i) => {
                let dotClass = 'carve-carousel-dot';
                const total = slides.length;
                let dist = Math.abs(i - current);
                if (dist > total / 2) dist = total - dist;
                if (i === current) dotClass += ' active';
                else if (dist === 1) dotClass += ' near-1';
                else if (dist === 2) dotClass += ' near-2';
                else dotClass += ' far';
                return (
                  <span
                    key={i}
                    className={dotClass}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goToSlide(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') goToSlide(i); }}
                    style={{ outline: 'none' }}
                  />
                );
              })}
            </div>
            <div className="carve-arrows-outer">
              <button className="carve-button-main" data-carve-carousel-button="prev" onClick={() => moveSlide(-1)}>&#10094;</button>
              <button className="carve-button-main" data-carve-carousel-button="next" onClick={() => moveSlide(1)}>&#10095;</button>
            </div>
          </div>
        </div>
      </div>
      <div className={`center-container ${showScanning ? 'scanning' : ''}`}>
        <div className='snippet' ref={snippetContainerRef}>
          <CarouselSnippet />
        </div>
        <button aria-label="Copy Button" onClick={copySnippet}>
          <Copy />
        </button>
      </div>
    </motion.div>
  );
};

const CarouselCard = () => {
  return (
    <Card
      CardTitle='Carousel'
      CardBody={<Carousel />}
    />
  );
};

export default CarouselCard;